<template>
  <div
    id="document-header"
    class="document-header d-flex w-100 justify-content-between align-items-center"
    data-testid="document-header"
  >
    <div class="d-flex flex-row">
      <i-button
        class="back-button i-mr-6"
        data-testid="results-panel-back-button"
        :left-icon="'medium-arrows-left'"
        :type="iButtonTypes.NEUTRAL"
        :button-style="iButtonStyles.GHOST"
        @button-click="$emit('back')"
      ></i-button>
      <div
        class="document-icon i-mr-6 d-flex align-items-center justify-content-center"
      >
        <i-key-icon
          class="i-text-primary-110"
          icon="small-file"
          :size="iKeyIconSize.SM"
          :shape="iKeyIconShape.SQUARE"
          :type="iKeyIconType.NEUTRAL"
          :icon-style="iKeyIconStyle.OUTLINE"
        ></i-key-icon>
      </div>
      <div class="d-flex flex-column">
        <span
          class="customer-name fs-exclude i-text-subheading-3"
          @click="
            $router.push({
              name: 'customer-overview',
              params: { customer_id: customer.id },
            })
          "
        >
          {{ customer.name }}
        </span>
        <div class="customer-second-line d-flex flex-row">
          <div
            class="document-switcher-trigger d-flex flex-row"
            @click="openSwitcher"
          >
            <span
              class="document-name fs-exclude i-text-button-2 i-text-neutral-120"
              data-testid="document-switcher-toggle"
            >
              {{ document.name }}
            </span>
            <div class="chevron">
              <i-icon
                symbol="small-chevron-down"
                class="i-text-neutral-80"
                :size="20"
              ></i-icon>
            </div>
          </div>
          <document-switcher
            class="document-switcher"
            :document="document"
            :customer="customer"
            :show="switcherOpen"
            @hide-drawer="switcherOpen = false"
          />
        </div>
      </div>
    </div>

    <div class="d-flex align-items-center">
      <!-- Compare-->
      <template>
        <i-button
          id="header-compare"
          data-testid="compare-documents-button"
          left-icon="medium-compare-text"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.GHOST"
          @button-click="openCompare"
        ></i-button>
        <i-tooltip
          target="#header-compare"
          title="Compare Documents"
          :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
        />
      </template>

      <!-- Similar Documents -->
      <i-button
        id="header-similar-documents"
        data-testid="similar-documents-button"
        left-icon="medium_search_file"
        :type="iButtonTypes.NEUTRAL"
        :button-style="iButtonStyles.GHOST"
        @button-click="openSimilarDocuments"
      ></i-button>
      <i-tooltip
        target="#header-similar-documents"
        title="Similar Documents"
        :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
      />

      <!-- API -->
      <i-button
        id="header-button-api"
        data-testid="view-document-api"
        :left-icon="'medium-file-code'"
        :type="iButtonTypes.NEUTRAL"
        :button-style="iButtonStyles.GHOST"
        @button-click="openAPIResponse"
      ></i-button>
      <i-tooltip
        target="#header-button-api"
        title="View API Response"
        :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
      />

      <i-button
        id="header-button-download"
        data-testid="download-document"
        v-read-only-check
        class="download-document-button"
        left-icon="medium-download-1"
        :type="iButtonTypes.NEUTRAL"
        :button-style="iButtonStyles.GHOST"
        @button-click="downloadDocument"
      ></i-button>
      <i-tooltip
        target="#header-button-download"
        title="Download"
        :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
      />

      <i-button
        id="header-button-delete"
        data-testid="delete-document"
        v-read-only-check
        class="delete-document-button"
        :left-icon="'medium-delete-bin-1'"
        :type="iButtonTypes.NEUTRAL"
        :button-style="iButtonStyles.GHOST"
        @button-click="tryDeleteDocument"
      ></i-button>
      <i-tooltip
        target="#header-button-delete"
        title="Delete"
        :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
      />

      <document-status-selector
        v-if="documentStatusEnabled"
        :document="document"
        class="i-ml-8"
      ></document-status-selector>
      <customer-approval-buttons
        v-else
        class="customer-approval-buttons"
        :customer="customer"
      />
      <chat-toggle-button v-if="isChatAgentEnabled" class="i-ml-6" />
    </div>
  </div>
</template>
<script>
import { downloadDocument } from '@/utils/download';
import CustomerApprovalButtons from '@/V2/customers/CustomerApprovalButtons';
import DocumentStatusSelector from '@/V2/documents/document-status/DocumentStatusSelector.vue';
import DocumentSwitcher from '@/V2/documents/DocumentSwitcher';
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iIcon,
  iKeyIcon,
  iKeyIconSize,
  iKeyIconShape,
  iKeyIconStyle,
  iKeyIconType,
  iTooltip,
  iTooltipAlignmentMode,
} from '@inscribe/signal';
import { mapActions, mapGetters } from 'vuex';
import ChatToggleButton from '@/V2/chat/ChatToggleButton.vue';
import { ADVANCED_READONLY_USER_ROLE } from '@/constants/userRoles';

export default {
  components: {
    CustomerApprovalButtons,
    DocumentStatusSelector,
    DocumentSwitcher,
    iButton,
    iIcon,
    iKeyIcon,
    iTooltip,
    ChatToggleButton,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
    customer: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isDownloading: false,
      isDeleting: false,
      switcherOpen: false,
      iButtonStyles,
      iButtonTypes,
      iTooltipAlignmentMode,
      iKeyIconSize,
      iKeyIconShape,
      iKeyIconStyle,
      iKeyIconType,
    };
  },
  computed: {
    ...mapGetters([
      'documentStatusEnabled',
      'isChatAgentEnabled',
      'getCachedCustomerById',
    ]),
    customerId() {
      return this.$route.params.customer_id;
    },
    documentId() {
      return this.$route.params.document_id;
    },
  },
  beforeUnmount() {
    this.switcherOpen = false;
  },
  methods: {
    ...mapActions({
      deleteDocument: 'deleteDocument',
    }),
    openSwitcher() {
      this.switcherOpen = true;
    },
    openCompare() {
      this.$router.push({
        name: 'document-compare',
        params: {
          customer_id: this.customerId,
        },
      });
    },
    openSimilarDocuments() {
      this.$emit('open-similar-documents');
    },
    openAPIResponse() {
      this.$router
        .push({
          name: 'document-api',
          params: {
            customer_id: this.customerId,
            document_id: this.documentId,
          },
        })
        .catch(() => {});
    },
    downloadDocument() {
      if (this.$store.state.currentUser.role === ADVANCED_READONLY_USER_ROLE) {
        return;
      }
      if (!this.isDownloading) {
        this.isDownloading = true;
        downloadDocument(
          this.document.urls.original_file,
          (this.$store.state.currentUser.is_superuser ? 'INSCRIBE_DOC-' : '') +
            this.document.name,
        ).then(() => {
          this.isDownloading = false;
        });
      }
    },
    async tryDeleteDocument() {
      this.$emit('document-deleted');
      await this.deleteDocument({
        customerId: this.customerId,
        document: this.document,
      });
      if (this.customerId) {
        this.$router.push({
          name: 'customer-overview',
          params: {
            customer_id: this.customerId,
          },
        });
      }
    },
    toggleHistory() {
      this.$emit('toggle-history');
    },
  },
};
</script>

<style lang="scss" scoped>
.document-header {
  height: 64px;
  z-index: 51;
  background: $color-white;
  border-bottom: 1px solid $color-almost-black-60;
  padding: 12px 24px 12px 12px;

  .customer-name {
    color: $light-neutral-110;
    padding-bottom: 2px;
    cursor: pointer;

    &:hover {
      color: $light-neutral-120;
    }
  }

  .document-switcher-trigger {
    align-items: center;
    cursor: pointer;

    .chevron {
      padding-left: 4px;
    }
  }

  .customer-approval-buttons {
    margin-left: 12px;
  }
}
</style>
